import React from 'react';
import { Controller, useFormContext, FieldPath } from 'react-hook-form';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { useAssetBucketing } from './useAssetBucketing';
import { useAccountFormContext } from '../../context/useAccountFormContext';
import { FormFields } from '../../AccountForm.meta';
import { AccountTaxStatusDTO } from '../../../../../../api/useAccountsQuery';
import { useAccountTaxStatusLabel } from '../../hooks/useAccountTaxStatusLabel';

const DEFAULT_LABEL = 'Asset Bucketing';

interface BaseProps {
  formKey?: FieldPath<FormFields>;
  taxStatus?: AccountTaxStatusDTO | null;
}

export const AssetBucketing = ({
  formKey = 'bankAccount.CustomAssetBucketIndex',
  taxStatus = null,
}: BaseProps) => {
  const {
    options: {
      AssetBucketingByManagementStyle: assetBucketingByManagementStyle,
      DisplayAssetBucketing,
    },
  } = useAccountFormContext();

  if (!DisplayAssetBucketing) {
    return null;
  }

  if (assetBucketingByManagementStyle) {
    return <AssetBucketingByManagementStyle formKey={formKey} taxStatus={taxStatus} />;
  }

  return <AssetBucketingClassic formKey={formKey} taxStatus={taxStatus} />;
};

interface Props {
  formKey?: FieldPath<FormFields>;
  taxStatus: AccountTaxStatusDTO | null;
}

export const AssetBucketingClassic = ({
  formKey = 'bankAccount.CustomAssetBucketIndex',
  taxStatus,
}: Props) => {
  const formBag = useFormContext<FormFields>();

  const {
    data: { allowAssetBucketingCustomization, options },
    actions: { updateHoldingsAssetBuckets },
  } = useAssetBucketing({ formKey, taxStatus });

  const { taxStatusLabel } = useAccountTaxStatusLabel(taxStatus);

  return (
    <Controller
      control={formBag.control}
      name={formKey}
      render={({ field: { value, ...field } }) => {
        return (
          <Select
            innerRef={field.ref}
            value={options.find(option => option.value === value) || null}
            options={options}
            onChange={opt => {
              if (opt) {
                // Order matter
                updateHoldingsAssetBuckets(opt.value);

                formBag.setValue(formKey, opt.value, {
                  shouldValidate: true,
                });
              }
            }}
            label={taxStatusLabel ? `${taxStatusLabel} ${DEFAULT_LABEL}` : DEFAULT_LABEL}
            placeholder={taxStatusLabel ? `${taxStatusLabel} ${DEFAULT_LABEL}` : DEFAULT_LABEL}
            aria-label={taxStatusLabel ? `${taxStatusLabel} ${DEFAULT_LABEL}` : DEFAULT_LABEL}
            disabled={field.disabled || !allowAssetBucketingCustomization}
            isPortal
          />
        );
      }}
    />
  );
};

const AssetBucketingByManagementStyle = ({
  formKey = 'bankAccount.CustomAssetBucketIndex',
  taxStatus,
}: Props) => {
  const formBag = useFormContext();

  const {
    data: { allowAssetBucketingCustomization, options, orionManagementStyleId },
  } = useAssetBucketing({ formKey, taxStatus });

  const { taxStatusLabel } = useAccountTaxStatusLabel(taxStatus);

  return (
    <Controller
      control={formBag.control}
      name={formKey}
      render={({ field: { value, ...field } }) => {
        const disabled =
          field.disabled || !allowAssetBucketingCustomization || orionManagementStyleId === null;

        return (
          <Select
            innerRef={field.ref}
            value={options.find(option => option.value === value) || null}
            options={options}
            onChange={opt => {
              if (opt) {
                formBag.setValue(formKey, opt.value);
              }
            }}
            label={taxStatusLabel ? `${taxStatusLabel} ${DEFAULT_LABEL}` : DEFAULT_LABEL}
            placeholder={disabled ? 'Select Management Style' : ''}
            aria-label={taxStatusLabel ? `${taxStatusLabel} ${DEFAULT_LABEL}` : DEFAULT_LABEL}
            disabled={disabled}
            isClearable
            isPortal
          />
        );
      }}
    />
  );
};
